(() => {
  const CONFIGS = [
    { title: 'rings collection', paths: ['/collections/rings-collection'], width: 205 },
    { title: 'chains', paths: ['/collections/chains'], width: 190 },
    { title: 'chain sets', paths: ['/collections/chain-sets'], width: 220 },
  ];

  const desktopQuery = window.matchMedia('(min-width: 750px)');
  const normalize = (value = '') => value.replace(/\s+/g, ' ').trim().toLowerCase();

  function getTrigger(item) {
    return item?.querySelector(':scope > [ref="menuitem"], :scope > a.menu-list__link') || null;
  }

  function getPanel(item) {
    return item?.querySelector(':scope > [ref="submenu[]"], :scope > .menu-list__submenu') || null;
  }

  function matchesConfig(trigger, config) {
    if (!trigger) return false;

    const title = normalize(trigger.textContent);
    let path = '';

    try {
      path = new URL(trigger.getAttribute('href') || '', window.location.origin).pathname
        .replace(/\/+$/, '')
        .toLowerCase();
    } catch (_) {
      path = '';
    }

    return title === config.title || config.paths.some((candidate) => path === candidate);
  }

  function getConfig(item) {
    const trigger = getTrigger(item);
    return CONFIGS.find((config) => matchesConfig(trigger, config));
  }

  function positionDropdown(item, width) {
    const panel = getPanel(item);
    if (!panel) return;

    const itemRect = item.getBoundingClientRect();
    const dropdownWidth = Math.min(width, Math.max(180, window.innerWidth - 24));
    const desiredViewportLeft = itemRect.left + itemRect.width / 2 - dropdownWidth / 2;
    const viewportLeft = Math.max(12, Math.min(desiredViewportLeft, window.innerWidth - dropdownWidth - 12));

    panel.style.setProperty('--sleek-dropdown-left', `${viewportLeft - itemRect.left}px`);
    panel.style.setProperty('--sleek-dropdown-width', `${dropdownWidth}px`);
  }

  function decorateItem(item, config) {
    const trigger = getTrigger(item);
    const panel = getPanel(item);
    if (!trigger || !panel || !config) return false;

    item.querySelectorAll(':scope > .sleek-category-dropdown__toggle').forEach((button) => button.remove());

    item.dataset.sleekDropdownMode = 'menu-name-click';
    item.classList.add('sleek-category-dropdown');
    trigger.classList.add('sleek-category-dropdown__trigger');
    trigger.setAttribute('aria-haspopup', 'true');
    panel.classList.add('sleek-category-dropdown__panel');

    const isOpen = item.classList.contains('sleek-category-dropdown--open');
    trigger.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
    panel.setAttribute('aria-hidden', isOpen ? 'false' : 'true');

    if (isOpen) {
      panel.setAttribute('data-active', '');
      positionDropdown(item, config.width);
    } else {
      panel.removeAttribute('data-active');
    }

    return true;
  }

  function closeItem(item) {
    if (!item) return;

    const trigger = getTrigger(item);
    const panel = getPanel(item);

    item.classList.remove('sleek-category-dropdown--open');
    trigger?.setAttribute('aria-expanded', 'false');
    panel?.setAttribute('aria-hidden', 'true');
    panel?.removeAttribute('data-active');
  }

  function closeAll(except = null) {
    document.querySelectorAll('.sleek-category-dropdown--open').forEach((item) => {
      if (item !== except) closeItem(item);
    });
  }

  function openItem(item) {
    const config = getConfig(item);
    if (!config || !decorateItem(item, config)) return;

    const trigger = getTrigger(item);
    const panel = getPanel(item);
    if (!trigger || !panel) return;

    closeAll(item);
    positionDropdown(item, config.width);
    item.classList.add('sleek-category-dropdown--open');
    trigger.setAttribute('aria-expanded', 'true');
    panel.setAttribute('aria-hidden', 'false');
    panel.setAttribute('data-active', '');
  }

  function toggleItem(item) {
    if (item.classList.contains('sleek-category-dropdown--open')) {
      closeItem(item);
    } else {
      openItem(item);
    }
  }

  function initializeAll() {
    if (!desktopQuery.matches) return;

    const header = document.querySelector('#header-component');
    if (!header) return;

    const items = [...header.querySelectorAll('.menu-list__list-item:not([slot="more"])')];

    CONFIGS.forEach((config) => {
      const item = items.find((candidate) => {
        const trigger = getTrigger(candidate);
        return trigger && getPanel(candidate) && matchesConfig(trigger, config);
      });

      if (item) decorateItem(item, config);
    });
  }

  // Capture the top-level click before the theme's hydrated header handler.
  // This survives Shopify section hydration because it is delegated on document.
  document.addEventListener('click', (event) => {
    if (!desktopQuery.matches || !(event.target instanceof Element)) return;

    const trigger = event.target.closest(
      '#header-component .menu-list__list-item > [ref="menuitem"], #header-component .menu-list__list-item > a.menu-list__link'
    );

    if (trigger) {
      const item = trigger.closest('.menu-list__list-item');
      const config = item ? getConfig(item) : null;

      if (item && config && getPanel(item)) {
        decorateItem(item, config);
        event.preventDefault();
        event.stopImmediatePropagation();
        toggleItem(item);
        return;
      }
    }

    if (!event.target.closest('#header-component .sleek-category-dropdown')) {
      closeAll();
    }
  }, true);

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') closeAll();
  });

  window.addEventListener('resize', () => {
    if (!desktopQuery.matches) {
      closeAll();
      return;
    }

    document.querySelectorAll('.sleek-category-dropdown--open').forEach((item) => {
      const config = getConfig(item);
      if (config) positionDropdown(item, config.width);
    });
  }, { passive: true });

  desktopQuery.addEventListener?.('change', () => {
    if (desktopQuery.matches) initializeAll();
    else closeAll();
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeAll, { once: true });
  } else {
    initializeAll();
  }

  document.addEventListener('shopify:section:load', initializeAll);

  // Header markup is replaced during Shopify section hydration. Watch deep
  // child replacements and re-apply the dropdown classes after hydration.
  const headerGroup = document.querySelector('#header-group');
  if (headerGroup) {
    let initQueued = false;
    const observer = new MutationObserver(() => {
      if (initQueued) return;
      initQueued = true;
      requestAnimationFrame(() => {
        initQueued = false;
        initializeAll();
      });
    });

    observer.observe(headerGroup, { childList: true, subtree: true });
  }
})();